// Recommendation Engine
// Generates dynamic recommendations based on current system state

export interface Recommendation {
  id: string
  title: string
  description: string
  priority: 'high' | 'medium' | 'low'
  category: 'content' | 'search' | 'optimization' | 'integration'
  actionText: string
  actionLink: string
  estimatedImpact: string
  estimatedTime: string
}

export interface SystemState {
  setupComplete: boolean
  hasLlmsTxt: boolean
  llmsTxtPublished: boolean // Check if LLMS.txt exists on the actual domain
  hasManifestJson: boolean
  contentCount: number
  enrichedContentCount: number
  searchQueriesCount: number
  hasBrandQueries: boolean
  hasProductQueries: boolean
  totalQueries: number
  llmsTxtLastUpdated?: Date
  domainConfigured: boolean
}

export class RecommendationEngine {
  private state: SystemState

  constructor(state: SystemState) {
    this.state = state
  }

  // Get all active recommendations
  getRecommendations(): Recommendation[] {
    const recommendations: Recommendation[] = []

    // Only show recommendations after setup is complete
    if (!this.state.setupComplete) {
      return []
    }

    // Check LLMS.txt published on domain
    if (this.state.hasLlmsTxt && !this.state.llmsTxtPublished) {
      recommendations.push({
        id: 'publish-llms',
        title: 'Publish LLMS.txt to Your Domain',
        description: 'Your LLMS.txt file is created but not yet published to your domain. Publish it to make your content discoverable by AI assistants.',
        priority: 'high',
        category: 'optimization',
        actionText: 'Copy or Download',
        actionLink: '/llms',
        estimatedImpact: 'High - Required for AI discovery',
        estimatedTime: '10-15 minutes'
      })
    }

    // Check content enrichment
    if (this.state.contentCount > 0) {
      const enrichmentPercentage = (this.state.enrichedContentCount / this.state.contentCount) * 100

      if (enrichmentPercentage < 100) {
        const unenrichedCount = this.state.contentCount - this.state.enrichedContentCount
        recommendations.push({
          id: 'complete-content-enrichment',
          title: 'Complete Content Enrichment',
          description: `You have ${unenrichedCount} content item${unenrichedCount > 1 ? 's' : ''} that need${unenrichedCount === 1 ? 's' : ''} enrichment. Fully enriched content significantly improves your AI Brand Index score.`,
          priority: enrichmentPercentage < 50 ? 'high' : 'medium',
          category: 'content',
          actionText: 'Enrich Content',
          actionLink: '/content',
          estimatedImpact: 'High - Directly improves Brand Index',
          estimatedTime: `${unenrichedCount * 2}-${unenrichedCount * 5} minutes`
        })
      }
    }

    // Check search queries - need 4+ total with brand and product coverage
    if (this.state.totalQueries < 3) {
      recommendations.push({
        id: 'add-search-queries',
        title: 'Create More Search Queries',
        description: `You have ${this.state.totalQueries} search ${this.state.totalQueries === 1 ? 'query' : 'queries'}. Add at least ${4 - this.state.totalQueries} more to get comprehensive visibility tracking.`,
        priority: 'high',
        category: 'search',
        actionText: 'Add Queries',
        actionLink: '/search',
        estimatedImpact: 'High - Better visibility insights',
        estimatedTime: '5-10 minutes'
      })
    }

    // Check if brand queries exist
    if (!this.state.hasBrandQueries && this.state.totalQueries < 10) {
      recommendations.push({
        id: 'add-brand-queries',
        title: 'Add Brand-Focused Queries',
        description: 'Track queries that mention your brand name to understand how AI assistants reference your company.',
        priority: 'medium',
        category: 'search',
        actionText: 'Add Brand Queries',
        actionLink: '/search',
        estimatedImpact: 'Medium - Brand awareness tracking',
        estimatedTime: '3-5 minutes'
      })
    }

    // Check if product queries exist
    if (!this.state.hasProductQueries && this.state.totalQueries < 10) {
      recommendations.push({
        id: 'add-product-queries',
        title: 'Add Product-Focused Queries',
        description: 'Track queries about your products or services to see when AI assistants recommend your offerings.',
        priority: 'medium',
        category: 'search',
        actionText: 'Add Product Queries',
        actionLink: '/search',
        estimatedImpact: 'Medium - Product visibility tracking',
        estimatedTime: '3-5 minutes'
      })
    }

    // Check LLMS.txt freshness (if older than 30 days)
    if (this.state.hasLlmsTxt && this.state.llmsTxtLastUpdated) {
      const daysSinceUpdate = Math.floor(
        (Date.now() - this.state.llmsTxtLastUpdated.getTime()) / (1000 * 60 * 60 * 24)
      )

      if (daysSinceUpdate > 30) {
        recommendations.push({
          id: 'update-llms',
          title: 'Update Your LLMS.txt File',
          description: `Your LLMS.txt was last updated ${daysSinceUpdate} days ago. Regular updates ensure AI assistants have your latest information.`,
          priority: daysSinceUpdate > 90 ? 'high' : 'low',
          category: 'content',
          actionText: 'Update LLMS.txt',
          actionLink: '/llms',
          estimatedImpact: 'Medium - Keeps content fresh',
          estimatedTime: '5-10 minutes'
        })
      }
    }

    // Sort by priority
    return this.sortByPriority(recommendations)
  }

  private sortByPriority(recommendations: Recommendation[]): Recommendation[] {
    const priorityOrder = { high: 0, medium: 1, low: 2 }
    return recommendations.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority])
  }

  // Get count by priority
  getRecommendationCounts(): { high: number; medium: number; low: number; total: number } {
    const recommendations = this.getRecommendations()
    return {
      high: recommendations.filter(r => r.priority === 'high').length,
      medium: recommendations.filter(r => r.priority === 'medium').length,
      low: recommendations.filter(r => r.priority === 'low').length,
      total: recommendations.length
    }
  }

  // Get recommendations by category
  getRecommendationsByCategory(category: string): Recommendation[] {
    return this.getRecommendations().filter(r => r.category === category)
  }
}
